import React, { useEffect, useRef, useState } from "react";
import { ModalDialog } from "react-bootstrap";
import styled from "styled-components";
import { colors } from "utils";
import { LogLevel, logEducatorDashboard } from "utils/sentryLogger";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import { PrimaryButtonLabel, primaryButtonStyles } from "./PrimaryButton";
import { CloseIcon } from "components/pages/WarmUpPicker/icons";
import { fetchClassWarmUpGroups, updateWarmUpGroupCodes } from "components/pages/WarmUpPicker/warmUpApi";
import {
  GRAY_BORDER,
  Message,
  TextButton,
  VisuallyHidden,
  WHITE,
  interText,
} from "components/pages/WarmUpPicker/styles";
import {
  Body,
  ButtonsRow,
  CloseButton,
  Directions,
  HeaderRow,
  InnerColumn,
  SetNameBlock,
  Title,
  WarmUpModal,
  WideSecondaryButton,
} from "components/pages/WarmUpPicker/modalParts";
import { AddChevronIcon, TrashIcon } from "./warmUpCardIcons";
import { useMenuButton } from "./useMenuButton";
import { Menu, MenuItem } from "./WarmUpGroupMenu";

// Edit Groups (Figma "Overlay/Edit Groups" 6463:8091): the warm-up's group
// codes, staged here and applied on Save in one change.

const TITLE_ID = "warm-up-edit-groups-title";

const LAST_CODE_TIP_ID = "warm-up-edit-groups-last-code-tip";
const LAST_CODE_TIP = "A warm-up needs at least one group code. To remove them all, archive the groups.";

const DIRECTIONS =
  "Adjust the number of groups and how long they'll work together. We recommend 2–5 students per group.";

// Plain string order of the stored codes, as the server lists them.
const byCode = (groups) => [...groups].sort((a, b) => (a.code < b.code ? -1 : Number(a.code > b.code)));

const MOST_CODES = 30;
const NO_FREE_CODES = "All group codes in this class are in use.";
const LOAD_FAILED = "We couldn't load the free group codes.";

const FAILED = "failed";
const MISSING = "missing";

const GONE = 404;
// The codes changed under the teacher: an added code is no longer free (409),
// or a code is no longer this class's (400).
const CODES_CHANGED = [409, 400];

const SAVE_FAILED = {
  codesChanged: "Some group codes changed while you were editing. Check the list and save again.",
  other: "Your changes were not saved. Try again.",
};

// The class's free codes, read on open: null while loading, "failed" when the
// read failed, "missing" when the answer carries no list of them (an API that
// does not list them yet), else the list. `reread(onRead)` reads them again
// and hands the new list to `onRead`. A 404 means the class is out of reach.
const useFreeGroupCodes = (classroomId, onGone) => {
  const [freeCodes, setFreeCodes] = useState(null);
  const [reads, setReads] = useState(0);
  const goneRef = useRef(onGone);
  goneRef.current = onGone;
  const onReadRef = useRef(null);

  useEffect(() => {
    let active = true;
    fetchClassWarmUpGroups(classroomId)
      .then(({ free_group_codes: codes }) => {
        if (!active) return;
        const list = Array.isArray(codes) ? codes : MISSING;
        setFreeCodes(list);
        if (Array.isArray(list)) onReadRef.current?.(list);
        onReadRef.current = null;
      })
      .catch((error) => {
        if (!active) return;
        if (error?.status === GONE) goneRef.current();
        else setFreeCodes(FAILED);
      });
    return () => {
      active = false;
    };
  }, [classroomId, reads]);

  const reread = (onRead = null) => {
    onReadRef.current = onRead;
    setFreeCodes(null);
    setReads((count) => count + 1);
  };

  return { freeCodes, reread };
};

const TABBABLE = 'button:not([disabled]):not([tabindex="-1"])';

// Tab from the last control goes back to the first, and Shift+Tab from the
// first to the last, so focus never leaves the dialog.
const keepTabInside = (event) => {
  if (event.key !== "Tab") return;
  const controls = Array.from(event.currentTarget.querySelectorAll(TABBABLE));
  const first = controls[0];
  const last = controls[controls.length - 1];
  const wrapTo = event.shiftKey ? last : first;
  if (document.activeElement !== (event.shiftKey ? first : last)) return;
  event.preventDefault();
  wrapTo.focus();
};

const isIn = (groups) => (group) => groups.some((g) => g.id === group.id);
const notIn = (groups) => (group) => !isIn(groups)(group);
const idsOf = (groups) => groups.map((g) => g.id);

// What Save sends: staged codes that were not the assignment's, and the
// assignment's codes no longer staged.
const changeOf = (original, staged) => ({
  addGroupIds: idsOf(staged.filter(notIn(original))),
  removeGroupIds: idsOf(byCode(original).filter(notIn(staged))),
});

// The class's free codes not yet added, plus the assignment's own codes
// removed here (so a code removed by mistake can be put back), in code order.
const offeredCodes = (freeCodes, original, staged) =>
  byCode([...freeCodes.filter(notIn(staged)), ...original.filter(notIn(staged))]);

// Save: nothing to send closes; else one PATCH, locked while in flight. A
// 200 hands the assignment back; a 404 means it is gone; a 409 or 400 means
// the codes changed meanwhile; anything else (after request's own retries)
// keeps the staged list with a message.
const useSaveChange = ({ assignment, classroomId, staged, onClose, onSaved, onGone, onCodesChanged }) => {
  const inFlight = useRef(false);
  const [saving, setSaving] = useState(false);
  const [saveFailure, setSaveFailure] = useState(null);

  const fail = (error) => {
    inFlight.current = false;
    setSaving(false);
    const codesChanged = CODES_CHANGED.includes(error?.status);
    logEducatorDashboard("Educator dashboard: Failed to save warm-up group codes", {
      level: LogLevel.WARNING,
      event: codesChanged ? "warm_up_edit_groups_conflict" : "warm_up_edit_groups_error",
      statusCode: error?.status,
      classroomId,
      assignmentId: assignment.id,
    });
    setSaveFailure(codesChanged ? SAVE_FAILED.codesChanged : SAVE_FAILED.other);
    if (codesChanged) onCodesChanged();
  };

  const save = () => {
    if (inFlight.current) return;
    const change = changeOf(assignment.groups, staged);
    if (change.addGroupIds.length === 0 && change.removeGroupIds.length === 0) {
      onClose();
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setSaveFailure(null);
    updateWarmUpGroupCodes(classroomId, assignment.id, change).then(
      (updated) => onSaved(updated, { addedCount: change.addGroupIds.length, removedCount: change.removeGroupIds.length }),
      (error) => (error?.status === GONE ? onGone() : fail(error))
    );
  };

  return { save, saving, inFlight, saveFailure };
};

export const EditWarmUpGroupsModal = ({ assignment, classroomId, onClose, onSaved, onGone }) => {
  const original = assignment.groups;
  const [staged, setStaged] = useState(() => byCode(original));
  const { freeCodes, reread } = useFreeGroupCodes(classroomId, onGone);
  const offered = Array.isArray(freeCodes) ? offeredCodes(freeCodes, original, staged) : [];
  const noneOffered = Array.isArray(freeCodes) && offered.length === 0;
  // After a conflict: the additions the class no longer offers are dropped.
  const dropAdditionsNotIn = (codes) =>
    setStaged((current) => current.filter((g) => isIn(original)(g) || isIn(codes)(g)));
  const { save, saving, inFlight, saveFailure } = useSaveChange({
    assignment,
    classroomId,
    staged,
    onClose,
    onSaved,
    onGone,
    onCodesChanged: () => reread(dropAdditionsNotIn),
  });
  const canAdd = !saving && offered.length > 0 && staged.length < MOST_CODES;
  const addMenu = useMenuButton({ idPrefix: "warm-up-free-codes", itemCount: offered.length });
  const [announcement, setAnnouncement] = useState("");
  const trashButtons = useRef({});
  const [focusId, setFocusId] = useState(null);

  useEffect(() => {
    if (focusId === null) return;
    trashButtons.current[focusId]?.focus();
    setFocusId(null);
  }, [focusId]);

  const lastCode = staged.length === 1;

  // Focus goes to the next row's trash, else the previous row's.
  const remove = (group) => {
    if (lastCode) return;
    const index = staged.findIndex((g) => g.id === group.id);
    const neighbour = staged[index + 1] || staged[index - 1];
    setStaged(staged.filter((g) => g.id !== group.id));
    setAnnouncement(`Group code ${group.code.toUpperCase()} removed`);
    setFocusId(neighbour.id);
  };

  const close = () => {
    if (!inFlight.current) onClose();
  };

  const add = (group) => {
    setStaged(byCode([...staged, group]));
    setAnnouncement(`Group code ${group.code.toUpperCase()} added`);
    setFocusId(group.id);
  };

  return (
    <WarmUpModal
      show
      onHide={close}
      centered
      restoreFocus={false}
      dialogAs={EditDialog}
      aria-labelledby={TITLE_ID}
      onKeyDown={keepTabInside}
    >
      <HeaderRow>
        <Title id={TITLE_ID}>Edit Groups</Title>
        <CloseButton type="button" aria-label="Close" onClick={close}>
          <CloseIcon fill={colors.nightlyWoods950} />
        </CloseButton>
      </HeaderRow>
      <Body>
        <InnerColumn>
          <SetNameBlock>{assignment.warmup_set.name}</SetNameBlock>
          <DirectionsBlock>
            <Directions>{DIRECTIONS}</Directions>
            <CodeList aria-label="Group codes">
              {staged.map((group) => (
                <CodeRow
                  key={group.id}
                  group={group}
                  buttonRef={(el) => {
                    trashButtons.current[group.id] = el;
                  }}
                  lastCode={lastCode}
                  disabled={saving}
                  onRemove={() => remove(group)}
                />
              ))}
            </CodeList>
            <AddWrapper {...addMenu.wrapperProps}>
              <AddButton {...addMenu.buttonProps} disabled={!canAdd}>
                <span>Add Group Code</span>
                <AddChevronIcon />
              </AddButton>
              {addMenu.isOpen && (
                <FreeCodesMenu {...addMenu.menuProps} aria-label="Free group codes">
                  <FreeCodesScroller $scrolls={offered.length > LIST_VISIBLE_ROWS}>
                    {offered.map((group, index) => (
                      <MenuItem key={group.id} {...addMenu.itemProps(index, () => add(group))}>
                        {group.code.toUpperCase()}
                      </MenuItem>
                    ))}
                  </FreeCodesScroller>
                </FreeCodesMenu>
              )}
            </AddWrapper>
            {saveFailure && <Message role="alert">{saveFailure}</Message>}
            {noneOffered && <Message role="status">{NO_FREE_CODES}</Message>}
            {freeCodes === FAILED && (
              <>
                <Message role="alert">{LOAD_FAILED}</Message>
                <TextButton type="button" onClick={() => reread()}>
                  <span>Try Again</span>
                </TextButton>
              </>
            )}
          </DirectionsBlock>
        </InnerColumn>
        <ButtonsRow>
          <WideSecondaryButton type="button" onClick={close}>
            <span>Cancel</span>
          </WideSecondaryButton>
          <SaveButton type="button" aria-busy={saving ? "true" : undefined} onClick={save}>
            <PrimaryButtonLabel>Save</PrimaryButtonLabel>
          </SaveButton>
        </ButtonsRow>
      </Body>
      <VisuallyHidden role="status" data-testid="edit-groups-status">
        {announcement}
      </VisuallyHidden>
    </WarmUpModal>
  );
};

// The last code's trash stays focusable but does nothing, and says why.
const CodeRow = ({ group, buttonRef, lastCode, disabled, onRemove }) => {
  const code = group.code.toUpperCase();
  const button = (
    <TrashButton
      type="button"
      ref={buttonRef}
      aria-label={`Remove group code ${code}`}
      disabled={disabled}
      aria-disabled={lastCode ? "true" : undefined}
      aria-describedby={lastCode ? LAST_CODE_TIP_ID : undefined}
      onClick={onRemove}
    >
      <TrashIcon />
    </TrashButton>
  );
  return (
    <Row>
      <Code>{code}</Code>
      {lastCode ? (
        <Tooltip id={LAST_CODE_TIP_ID} tip={LAST_CODE_TIP}>
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </Row>
  );
};

// The box does not clip: the free codes list hangs below it.
const EditDialog = styled(ModalDialog)`
  &.modal-dialog {
    --bs-modal-width: 600px;
  }

  & .modal-content {
    background: ${WHITE};
    border: none;
    border-radius: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 24px;
    overflow: visible;
    padding: 32px;
  }
`;

const DirectionsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CodeList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Row = styled.li`
  align-items: center;
  border-bottom: 1px solid ${GRAY_BORDER};
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  padding: 16px 24px;
`;

const Code = styled.span`
  ${interText(18, 26, 700)}
  color: ${colors.nightlyWoods950};
  text-transform: uppercase;
  white-space: nowrap;
`;

const TrashButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  height: 24px;
  padding: 0;
  width: 24px;

  &[aria-disabled="true"],
  &:disabled {
    cursor: default;
  }
`;

const AddWrapper = styled.div`
  align-self: flex-start;
  display: flex;
  position: relative;
`;

// Figma "Button/Text Only". Its width is Figma's: Chrome sets Inter a little
// wider or narrower than Figma does.
const ADD_BUTTON_WIDTH = 198;

const AddButton = styled.button`
  align-items: center;
  align-self: flex-start;
  background: transparent;
  border: none;
  border-radius: 6px;
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  height: 48px;
  padding: 0 8px 0 16px;
  width: ${ADD_BUTTON_WIDTH}px;

  & > span {
    ${interText(18, 26)}
    color: ${colors.blue700};
    padding: 0 4px;
    white-space: nowrap;
  }

  &:disabled {
    cursor: default;
  }

  &:disabled > span {
    color: ${GRAY_BORDER};
  }

  &:disabled path {
    stroke: ${GRAY_BORDER};
  }
`;

const LIST_ROW_HEIGHT = 40;
const LIST_VISIBLE_ROWS = 5;

// The card menu's box (its stroke drawn over the rows), opened under Add's
// left edge.
const FreeCodesMenu = styled(Menu)`
  left: 0;
  right: auto;
`;

const LIST_STROKE_WIDTH = 1;

// Past five codes the rows scroll in a box inset by the stroke on every side,
// so the scrollbar sits inside the border while the list stays five rows tall.
const FreeCodesScroller = styled.div`
  ${({ $scrolls }) =>
    $scrolls &&
    `
    margin: ${LIST_STROKE_WIDTH}px;
    max-height: ${LIST_VISIBLE_ROWS * LIST_ROW_HEIGHT - 2 * LIST_STROKE_WIDTH}px;
    overflow-y: auto;
  `}
`;

const SaveButton = styled.button`
  ${primaryButtonStyles}
  display: inline-flex;
  justify-content: center;
  padding: 0 16px;
  width: 180px;
`;
